/**
 * Swatch picker for text colour and highlight (#279), shared by the toolbar
 * and the selection bar's Format mode.
 *
 * Presets come first, then the user's saved colours (stored in the
 * `editor.customColors` preference so they follow them across devices), then
 * a native colour input to apply - and optionally save - any other colour.
 */

import { useCallback, useState } from 'react';
import { X } from 'lucide-react';
import { usePreferencesOptional } from '../../contexts/PreferencesContext';
import {
  CUSTOM_COLORS_PREF,
  HIGHLIGHT_PRESETS,
  TEXT_COLOR_PRESETS,
  addCustomColor,
  parseCustomColors,
  removeCustomColor,
  serializeCustomColors,
  toHighlightColor,
} from './formatColors';

export type ColorKind = 'text' | 'highlight';

/** The user's saved colours. Works without a PreferencesProvider (kept in memory only). */
export function useCustomColors() {
  const prefsCtx = usePreferencesOptional();
  const [local, setLocal] = useState<string[]>([]);
  const colors = prefsCtx ? parseCustomColors(prefsCtx.prefs[CUSTOM_COLORS_PREF]) : local;

  const persist = useCallback((next: string[]) => {
    if (prefsCtx) {
      prefsCtx.update({ [CUSTOM_COLORS_PREF]: serializeCustomColors(next) }).catch(() => {
        // update() already reloads the server copy on failure.
      });
    } else {
      setLocal(next);
    }
  }, [prefsCtx]);

  return {
    colors,
    save: (c: string) => persist(addCustomColor(colors, c)),
    remove: (c: string) => persist(removeCustomColor(colors, c)),
  };
}

interface ColorPickerProps {
  kind: ColorKind;
  /** The colour currently applied to the selection, if any. */
  active: string | null;
  /** Apply a colour, or remove it with null. */
  onPick: (color: string | null) => void;
}

export function ColorPicker({ kind, active, onPick }: ColorPickerProps) {
  const { colors: saved, save, remove } = useCustomColors();
  const [custom, setCustom] = useState('#6366f1');
  const presets = kind === 'highlight' ? HIGHLIGHT_PRESETS : TEXT_COLOR_PRESETS;
  const noun = kind === 'highlight' ? 'Highlight' : 'Text colour';
  const applied = (c: string) => (kind === 'highlight' ? toHighlightColor(c) : c);

  return (
    <div className="p-2 w-[212px]" data-testid={`color-picker-${kind}`}>
      <div className="grid grid-cols-6 gap-1">
        <Swatch kind={kind} label={`No ${noun.toLowerCase()}`} color={null} active={!active} onClick={() => onPick(null)} />
        {presets.map((p) => (
          <Swatch
            key={p.value}
            kind={kind}
            label={`${noun} ${p.name}`}
            color={p.value}
            active={active === p.value}
            onClick={() => onPick(p.value)}
          />
        ))}
      </div>

      {saved.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wide text-text-muted mt-2 mb-1">Saved</p>
          <div className="grid grid-cols-6 gap-1">
            {saved.map((c) => (
              <div key={c} className="relative group">
                <Swatch
                  kind={kind}
                  label={`${noun} saved ${c}`}
                  color={applied(c)}
                  active={active === applied(c)}
                  onClick={() => onPick(applied(c))}
                />
                <button
                  type="button"
                  aria-label={`Remove saved colour ${c}`}
                  title="Remove saved colour"
                  onClick={() => remove(c)}
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-bg-card border border-border text-text-muted hover:text-danger flex items-center justify-center opacity-70 sm:opacity-0 sm:group-hover:opacity-100"
                >
                  <X size={8} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border">
        <input
          type="color"
          aria-label={`Custom ${noun.toLowerCase()}`}
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          className="w-7 h-7 p-0 border border-border rounded bg-transparent cursor-pointer"
        />
        <button
          type="button"
          onClick={() => onPick(applied(custom))}
          className="flex-1 px-2 py-1 text-xs rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-hover"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => { save(custom); onPick(applied(custom)); }}
          className="flex-1 px-2 py-1 text-xs rounded-md bg-accent/20 text-accent-hover hover:bg-accent/30"
        >
          Save
        </button>
      </div>
    </div>
  );
}

function Swatch({ kind, label, color, active, onClick }: {
  kind: ColorKind;
  label: string;
  color: string | null;
  active: boolean;
  onClick: () => void;
}) {
  const style = color
    ? kind === 'highlight' ? { backgroundColor: color } : { color }
    : undefined;
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={onClick}
      style={style}
      className={`w-7 h-7 rounded-md border text-xs font-semibold flex items-center justify-center transition-colors
        ${active ? 'border-accent ring-1 ring-accent' : 'border-border hover:border-text-muted'}
        ${!color ? 'text-text-muted line-through' : kind === 'highlight' ? 'text-text-primary' : ''}`}
    >
      A
    </button>
  );
}
